import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, X } from "lucide-react";
import { studio } from "@/lib/studioApi";
import { PushToggle } from "@/components/studio/PushToggle";

const ago = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 172800) return "hier";
  if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} jours`;
  return new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const NotificationBell = ({ testId = "notification-bell" }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const panel = useRef(null);

  const load = () => studio.get("/notifications").then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener("devisto:notifications", load);
    return () => { clearInterval(timer); window.removeEventListener("devisto:notifications", load); };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (box.current && !box.current.contains(e.target) && !(panel.current && panel.current.contains(e.target))) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", outside);
    document.addEventListener("touchstart", outside);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", outside); document.removeEventListener("touchstart", outside); document.removeEventListener("keydown", esc); };
  }, [open]);

  const markAll = async () => {
    await studio.post("/notifications/read").catch(() => {});
    setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, read: true })) }));
    window.dispatchEvent(new Event("devisto:notifications"));
  };

  const close = () => { setOpen(false); if (data.unread) markAll(); };
  const go = (n) => { close(); if (n.target_url) navigate(n.target_url); };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => (open ? close() : setOpen(true))}
        aria-label="Notifications"
        aria-expanded={open}
        className={`relative rounded-full border p-2 transition-colors duration-200 ${
          data.unread ? "border-red-200 bg-red-50 text-red-600" : "border-[#E2E7F7] bg-white text-[#93A0BC] hover:text-[#3A5CC8]"
        }`}
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span data-testid="notification-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panel}
          data-testid={`${testId}-panel`}
          role="dialog"
          aria-label="Notifications"
          className="dv-card-in fixed inset-x-3 top-[72px] z-[60] flex max-h-[min(72vh,640px)] flex-col overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_24px_60px_-20px_rgba(11,30,61,0.35)] lg:inset-x-auto lg:left-[284px] lg:top-4 lg:w-[400px]"
        >
          <div className="flex items-center justify-between gap-2 border-b border-[#EEF1FC] px-4 py-3">
            <p className="inline-flex min-w-0 items-center gap-2 font-heading text-base font-extrabold text-[#0F2645]">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-500"><Bell className="h-4 w-4" strokeWidth={2.2} /></span>
              <span className="truncate">Notifications</span>
              {data.unread > 0 && <span className="shrink-0 whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-600">{data.unread}</span>}
            </p>
            <div className="flex shrink-0 items-center gap-1">
              {data.unread > 0 && (
                <button type="button" onClick={markAll} data-testid="notifications-mark-all" className="inline-flex min-h-[36px] shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-2 text-xs font-semibold text-[#3A5CC8] hover:bg-[#EEF1FC]">
                  <CheckCheck className="h-3.5 w-3.5" /> Tout lu
                </button>
              )}
              <button type="button" onClick={close} aria-label="Fermer" data-testid="notifications-close" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#5C6A87] hover:bg-[#F3F5FC]">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div data-testid="notifications-list" className="flex-1 overflow-y-auto overscroll-contain">
            {data.items.length ? data.items.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`notification-${n.id}`}
                onClick={() => go(n)}
                className={`relative block w-full border-b border-[#EEF1FC] px-4 py-3 pr-9 text-left transition-colors duration-150 last:border-b-0 hover:bg-[#F6F8FD] ${n.read ? "bg-white" : "bg-[#F3F6FF]"}`}
              >
                <p className={`whitespace-normal break-words text-sm leading-snug text-[#0F2645] ${n.read ? "font-medium" : "font-semibold"}`}>{n.label}</p>
                <p className="mt-1 text-xs text-[#93A0BC]">{ago(n.created_at)}</p>
                {!n.read && <span aria-hidden="true" className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-[#3A5CC8]" />}
              </button>
            )) : (
              <p className="px-4 py-10 text-center text-sm text-[#5C6A87]">Aucune notification pour l'instant.</p>
            )}
          </div>

          <div className="shrink-0 border-t border-[#EEF1FC] bg-[#FAFBFF]">
            <PushToggle compact />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
